import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function Login({ onLogin, onSwitchToRegister, onForgotPassword }) {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [version, setVersion] = useState('1.0.1');
  const [serverStatus, setServerStatus] = useState('checking'); // 'online', 'offline', 'checking', 'maintenance'
  const [maintenanceMessage, setMaintenanceMessage] = useState('');

  useEffect(() => {
    // Fetch version from API
    axios.get('/api/settings/footer')
      .then(response => {
        setVersion(response.data.version || '1.0.1');
      })
      .catch(() => {
        // If API fails, use default version
        setVersion('1.0.1');
      });
    
    // Check server status
    checkServerStatus();
    
    // Check server status every 10 seconds (faster during maintenance)
    const statusInterval = setInterval(checkServerStatus, 10000);
    return () => clearInterval(statusInterval);
  }, []);

  const checkServerStatus = async () => {
    try {
      // First check maintenance status
      const maintenanceRes = await axios.get('/api/maintenance/status', { timeout: 5000 });
      if (maintenanceRes.data && maintenanceRes.data.maintenance) {
        setServerStatus('maintenance');
        setMaintenanceMessage(maintenanceRes.data.message || 'System is under maintenance.');
        return;
      }
      
      // Then check health
      const response = await axios.get('/api/health', { timeout: 5000 });
      if (response.data && response.data.status === 'ok') {
        setServerStatus('online');
        setMaintenanceMessage('');
      } else {
        setServerStatus('offline');
      }
    } catch (error) {
      setServerStatus('offline');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE}/auth/login`,
        formData,
        { withCredentials: true }
      );
      
      if (onLogin) {
        onLogin(response.data);
      }
    } catch (err) {
      // Handle maintenance mode error specifically
      if (err.response?.data?.maintenance) {
        setError('System is under maintenance. Only administrators can log in during this time.');
        setServerStatus('maintenance');
        setMaintenanceMessage(err.response.data.message);
      } else {
        const errorMessage = err.response?.data?.error || 'Login failed. Please try again.';
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>Esfincter</h1>
        <h2>Login</h2>
        
        {serverStatus === 'maintenance' && (
          <div style={{
            background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
            color: '#fff',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            textAlign: 'center',
            fontSize: '0.9rem'
          }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>🔧</div>
            <strong>Maintenance Mode Active</strong>
            <div style={{ marginTop: '0.5rem', opacity: 0.9, fontSize: '0.85rem' }}>
              {maintenanceMessage || 'System is under maintenance.'}
            </div>
            <div style={{ marginTop: '0.5rem', opacity: 0.8, fontSize: '0.8rem' }}>
              Administrators can still log in.
            </div>
          </div>
        )}
        
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">Username or Email</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              autoComplete="current-password"
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </div>
        </form>

        {onForgotPassword && (
          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <button 
              type="button" 
              className="link-button"
              onClick={onForgotPassword}
              style={{ fontSize: '0.9rem', color: '#999' }}
            >
              Forgot your password?
            </button>
          </div>
        )}

        {serverStatus !== 'maintenance' && (
          <div className="auth-switch">
            <p>
              Don't have an account?{' '}
              <button 
                type="button" 
                className="link-button"
                onClick={onSwitchToRegister}
              >
                Register here
              </button>
            </p>
          </div>
        )}
        <div style={{ textAlign: 'center', marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #444' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span className={`status-indicator ${serverStatus === 'maintenance' ? 'maintenance' : serverStatus}`}></span>
            <span style={{ color: serverStatus === 'maintenance' ? '#f7931e' : '#666', fontSize: '0.8rem' }}>
              {serverStatus === 'online' ? 'Server Online' : 
               serverStatus === 'maintenance' ? '🔧 Maintenance' :
               serverStatus === 'offline' ? 'Server Offline' : 'Checking...'}
            </span>
            <span style={{ color: '#666', fontSize: '0.8rem' }}>•</span>
            <span style={{ color: '#666', fontSize: '0.8rem', margin: 0 }}>
              Version {version}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;

